import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Grid, Paper, Typography, CircularProgress, Button,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Fade, TableContainer
} from '@mui/material';
import {
  ShoppingBagRounded, PeopleAltRounded, Inventory2Rounded, CurrencyRupeeRounded,
  ArrowForwardRounded, AddRounded, WebRounded, CategoryRounded
} from '@mui/icons-material';
import api from '../api';
import { getAdminName } from '../auth';
import { BRAND } from '../theme';

const STATUS_COLOR = { paid: 'success', pending: 'warning', failed: 'error', refunded: 'default' };

// List endpoints return { success, data: [...], pagination: { total } }; orders return a plain array.
const countOf = (body) => {
  if (Array.isArray(body)) return body.length;
  if (body?.pagination?.total !== undefined) return body.pagination.total;
  return Array.isArray(body?.data) ? body.data.length : 0;
};

const QUICK_LINKS = [
  { label: 'Add a product',   to: '/products',   icon: <AddRounded /> },
  { label: 'Manage templates', to: '/template',  icon: <WebRounded /> },
  { label: 'Edit categories', to: '/categories', icon: <CategoryRounded /> },
  { label: 'View orders',     to: '/orders',     icon: <ShoppingBagRounded /> },
];

function StatCard({ label, value, icon, tint, loading }) {
  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: '20px', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary', fontWeight: 500 }}>{label}</Typography>
        <Box sx={{
          width: 44, height: 44, borderRadius: '14px', background: tint,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 22, color: '#fff' } })}
        </Box>
      </Box>
      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
      )}
    </Paper>
  );
}

const Dashboard = () => {
  const [stats, setStats] = useState({ orders: 0, revenue: 0, products: 0, users: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/payments/admin/all-orders').then(r => r.data).catch(() => []),
      api.get('/api/products', { params: { limit: 1 } }).then(r => r.data).catch(() => null),
      api.get('/api/users', { params: { limit: 1 } }).then(r => r.data).catch(() => null),
    ]).then(([orders, products, users]) => {
      const orderList = Array.isArray(orders) ? orders : [];
      const revenue = orderList
        .filter(o => o.status === 'paid')
        .reduce((sum, o) => sum + (o.amount || 0), 0);
      setStats({
        orders: orderList.length,
        revenue,
        products: countOf(products),
        users: countOf(users),
      });
      setRecentOrders(orderList.slice(0, 6));
    }).finally(() => setLoading(false));
  }, []);

  const firstName = getAdminName().split(' ')[0];

  return (
    <Fade in timeout={400}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Welcome hero */}
        <Paper elevation={0} sx={{
          position: 'relative', overflow: 'hidden', borderRadius: '24px', color: '#fff',
          background: BRAND.heroGradient, p: { xs: 3, md: 5 },
        }}>
          <Box sx={{
            position: 'absolute', width: 340, height: 340, borderRadius: '50%', right: -80, top: -140,
            background: 'radial-gradient(circle, rgba(123,63,242,0.6), transparent 70%)',
          }} />
          <Box sx={{ position: 'relative', maxWidth: 560 }}>
            <Typography sx={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.65, mb: 1 }}>
              Dashboard
            </Typography>
            <Typography variant="h4" sx={{ mb: 1 }}>Welcome back, {firstName}</Typography>
            <Typography sx={{ opacity: 0.7, mb: 3 }}>
              Here is how your stores are doing today.
            </Typography>
            <Button
              component={Link}
              to="/orders"
              variant="contained"
              endIcon={<ArrowForwardRounded />}
              sx={{ bgcolor: '#fff', background: '#fff', color: BRAND.ink, '&:hover': { background: '#f2f1ff' } }}
            >
              View orders
            </Button>
          </Box>
        </Paper>

        {/* Stat cards */}
        <Grid container spacing={3}>
          {[
            { label: 'Total orders',   value: stats.orders, icon: <ShoppingBagRounded />, tint: BRAND.gradient },
            { label: 'Revenue (paid)', value: `₹${stats.revenue.toLocaleString('en-IN')}`, icon: <CurrencyRupeeRounded />, tint: 'linear-gradient(135deg,#1fbf8f,#12a37a)' },
            { label: 'Products',       value: stats.products, icon: <Inventory2Rounded />, tint: 'linear-gradient(135deg,#ffad33,#ff8a3d)' },
            { label: 'Users',          value: stats.users, icon: <PeopleAltRounded />, tint: 'linear-gradient(135deg,#3d7bff,#2a5be0)' },
          ].map(card => (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={card.label}>
              <StatCard {...card} loading={loading} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Recent orders */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Paper elevation={1} sx={{ borderRadius: '20px', overflow: 'hidden', height: '100%' }}>
              <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: 17 }}>Recent orders</Typography>
                  <Typography variant="body2" color="text.secondary">Latest 6 orders across all stores</Typography>
                </Box>
                <Button component={Link} to="/orders" size="small" endIcon={<ArrowForwardRounded />}>
                  See all
                </Button>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : recentOrders.length === 0 ? (
                <Typography sx={{ px: 3, pb: 4, color: 'text.secondary' }}>No orders yet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Business</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentOrders.map(order => (
                        <TableRow key={order._id} hover sx={{ '& td': { py: 1.5 } }}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                            #{order._id?.slice(-8)}
                          </TableCell>
                          <TableCell>{order.business?.name || '—'}</TableCell>
                          <TableCell>{order.user?.name || order.user?.email || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>₹{order.amount}</TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                              color={STATUS_COLOR[order.status] || 'default'}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize', fontSize: 11 }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: 12.5 }}>
                            {new Date(order.createdAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Quick actions */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper elevation={1} sx={{ borderRadius: '20px', p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontSize: 17, mb: 2 }}>Quick actions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {QUICK_LINKS.map(q => (
                  <Box
                    key={q.to}
                    component={Link}
                    to={q.to}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '14px',
                      border: '1px solid', borderColor: 'divider', color: 'text.primary', textDecoration: 'none',
                      transition: 'border-color .15s, background-color .15s',
                      '&:hover': { borderColor: BRAND.violet, bgcolor: '#f7f5ff' },
                    }}
                  >
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2.5, bgcolor: '#f2f1ff', color: BRAND.violet,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {React.cloneElement(q.icon, { sx: { fontSize: 20 } })}
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, flexGrow: 1 }}>{q.label}</Typography>
                    <ArrowForwardRounded sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
};

export default Dashboard;
